import { useState, useCallback, useEffect } from "react";
import { ethers } from "ethers";
import {
	useAccount,
	useConnect,
	useDisconnect,
	useSwitchChain,
	useConnectors,
	useBalance,
} from "wagmi";
import type { SwitchNetworkParams } from "../types/contract";

export interface Wallet {
	id: string;
	name: string;
	icon: string;
}

interface UseWalletReturn {
	account: string | null;
	isConnected: boolean;
	provider: ethers.BrowserProvider | null;
	isConnecting: boolean;
	error: string | null;
	balanceData?: {
		decimals: number;
		formatted: string;
		symbol: string;
		value: bigint;
	};
	connect: () => Promise<void>;
	disconnect: () => Promise<void>;
	switchNetwork: (params: SwitchNetworkParams) => Promise<void>;
	getAvailableWallets: () => Wallet[];
}

export const useWallet = (): UseWalletReturn => {
	const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
	const [error, setError] = useState<string | null>(null);

	const { address, isConnected } = useAccount();
	const { connect: wagmiConnect, isPending } = useConnect();
	const { disconnect: wagmiDisconnect } = useDisconnect();
	const { switchChain } = useSwitchChain();
	const { data: balanceData } = useBalance({
		address: address,
		chainId: 7080969,
	});
	const connectors = useConnectors();

	const getAvailableWallets = useCallback((): Wallet[] => {
		const wallets: Wallet[] = [];
		const addedWalletIds = new Set<string>();

		// Filter to only show MetaMask wallets
		const allowedConnectors = connectors.filter((connector) => {
			const name = connector.name.toLowerCase();
			return name.includes("metamask");
		});

		allowedConnectors.forEach((connector) => {
			const name = connector.name.toLowerCase();
			if (name.includes("metamask") && !addedWalletIds.has("metamask")) {
				wallets.push({
					id: "metamask",
					name: "MetaMask",
					icon: "https://raw.githubusercontent.com/MetaMask/brand-resources/master/SVG/metamask-fox.svg",
				});
				addedWalletIds.add("metamask");
			}
		});

		return wallets;
	}, [connectors]);

	const connect = useCallback(async (): Promise<void> => {
		if (isPending) return;

		setError(null);

		try {
			console.log(
				"Available connectors:",
				connectors.map((c) => ({ id: c.id, name: c.name, type: c.type })),
			);

			// Find MetaMask connector
			const targetConnector = connectors.find((connector) => {
				const name = connector.name.toLowerCase();
				const id = connector.id.toLowerCase();
				return (
					name.includes("metamask") ||
					id.includes("metamask") ||
					connector.type === "metaMask"
				);
			});

			console.log("Looking for MetaMask connector, found:", targetConnector);

			if (!targetConnector) {
				throw new Error(
					"MetaMask not found. Please install MetaMask extension.",
				);
			}

			console.log("Attempting to connect with connector:", targetConnector);
			wagmiConnect({ connector: targetConnector });
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to connect wallet";
			console.error("Failed to connect wallet:", err);
			setError(errorMessage);
		}
	}, [isPending, connectors, wagmiConnect]);

	const disconnect = useCallback(async (): Promise<void> => {
		wagmiDisconnect();
		setProvider(null);
		setError(null);
	}, [wagmiDisconnect]);

	const switchNetwork = useCallback(
		async (params: SwitchNetworkParams): Promise<void> => {
			if (!isConnected) {
				throw new Error("No wallet connected");
			}

			try {
				// Extract chain ID number from hex string
				const chainId = parseInt(params.chainId, 16) as
					| 1
					| 11155111
					| 42161
					| 137
					| 31337;
				switchChain({ chainId });
			} catch (switchError) {
				const errorMessage =
					switchError instanceof Error
						? switchError.message
						: "Failed to switch network";
				throw new Error(`Failed to switch network: ${errorMessage}`);
			}
		},
		[isConnected, switchChain],
	);

	// Update provider when connected
	useEffect(() => {
		const updateProvider = async () => {
			if (isConnected && typeof window !== "undefined" && window.ethereum) {
				try {
					const ethersProvider = new ethers.BrowserProvider(window.ethereum);
					setProvider(ethersProvider);
				} catch (err) {
					console.error("Failed to create ethers provider:", err);
					setProvider(null);
				}
			} else {
				setProvider(null);
			}
		};

		updateProvider();
	}, [isConnected]);

	return {
		account: address || null,
		isConnected,
		provider,
		isConnecting: isPending,
		error,
		connect,
		disconnect,
		switchNetwork,
		getAvailableWallets,
		balanceData,
	};
};
